/**
 * Menu authoring — Dishes / Sections / Menus / Labels / Combos, backed by SK
 * Catalog through the adapter's /menu-admin surface. Operator vocabulary only;
 * Catalog jargon (item-type/collection/relation) stays hidden.
 */
import { apiClient } from '../../api/axiosInstance';
import {
  Hosp_MenuAdmin_Provision_Url, Hosp_MenuAdmin_Items_Url, Hosp_MenuAdmin_Item_Url,
  Hosp_MenuAdmin_Item_Publish_Url, Hosp_MenuAdmin_Sections_Url, Hosp_MenuAdmin_Section_Url,
  Hosp_MenuAdmin_Item_Sections_Url, Hosp_MenuAdmin_Menus_Url, Hosp_MenuAdmin_Menu_Url,
  Hosp_MenuAdmin_Menu_Items_Url, Hosp_MenuAdmin_Menu_Item_Url, Hosp_MenuAdmin_Labels_Url,
  Hosp_MenuAdmin_Label_Url, Hosp_MenuAdmin_Item_Labels_Url, Hosp_MenuAdmin_Item_Combo_Url,
  Hosp_MenuAdmin_Item_Combo_Child_Url,
} from '../../api/endpoints';

export type FoodType = 'veg' | 'non_veg' | 'egg' | 'vegan';
export interface DishInput {
  name: string; price: number; foodType: FoodType; allergens: string[];
  description?: string; spice?: string;
}
export interface Section { id: string; name: string }
export interface MenuGroup { id: string; name: string }

// FSSAI/EU-14 allergen vocabulary (UHMS §5.4) — the multi-select options.
export const ALLERGENS = [
  'gluten', 'crustaceans', 'eggs', 'fish', 'peanuts', 'soybeans', 'milk',
  'tree_nuts', 'celery', 'mustard', 'sesame', 'sulphites', 'lupin', 'molluscs',
];

const dishFields = (d: DishInput) => [
  { fieldKey: 'name', value: d.name },
  { fieldKey: 'base_price', value: d.price },
  { fieldKey: 'food_type', value: d.foodType },
  { fieldKey: 'allergens', value: d.allergens },
  ...(d.description ? [{ fieldKey: 'description', value: d.description }] : []),
  ...(d.spice ? [{ fieldKey: 'spice_level', value: d.spice }] : []),
];

// Idempotent — ensures the "Menu Item" type + UHMS fields exist for this venue.
export const provisionMenu = () => apiClient.post(Hosp_MenuAdmin_Provision_Url, { nutritionRequired: false });

export const createDish = async (itemTypeId: string, d: DishInput): Promise<{ id: string }> => {
  const { data } = await apiClient.post(Hosp_MenuAdmin_Items_Url, { itemTypeId, fields: dishFields(d) });
  return data;
};
export const updateDish = (id: string, d: DishInput) =>
  apiClient.patch(Hosp_MenuAdmin_Item_Url(id), { fields: dishFields(d) });
export const publishDish = (id: string) => apiClient.post(Hosp_MenuAdmin_Item_Publish_Url(id));

// Sections
export const listSections = async (): Promise<Section[]> => {
  const { data } = await apiClient.get(Hosp_MenuAdmin_Sections_Url);
  const flat: Section[] = [];
  const walk = (ns: any[]) => (ns || []).forEach((n) => { if (n?.id) flat.push({ id: n.id, name: n.name }); if (n.children) walk(n.children); });
  walk(Array.isArray(data) ? data : data?.items ?? []);
  return flat;
};
export const createSection = (name: string) => apiClient.post(Hosp_MenuAdmin_Sections_Url, { name });
export const renameSection = (id: string, name: string) => apiClient.patch(Hosp_MenuAdmin_Section_Url(id), { name });
export const deleteSection = (id: string) => apiClient.delete(Hosp_MenuAdmin_Section_Url(id));
export const setDishSections = (id: string, categoryIds: string[]) => apiClient.put(Hosp_MenuAdmin_Item_Sections_Url(id), { categoryIds });

// Menus
export const listMenus = async (): Promise<MenuGroup[]> => {
  const { data } = await apiClient.get(Hosp_MenuAdmin_Menus_Url);
  const arr = Array.isArray(data) ? data : data?.items ?? [];
  return arr.filter((m: any) => m?.id).map((m: any) => ({ id: m.id, name: m.name }));
};
export const createMenuGroup = (name: string) => apiClient.post(Hosp_MenuAdmin_Menus_Url, { name });
export const renameMenuGroup = (id: string, name: string) => apiClient.patch(Hosp_MenuAdmin_Menu_Url(id), { name });
export const deleteMenuGroup = (id: string) => apiClient.delete(Hosp_MenuAdmin_Menu_Url(id));
export const addDishesToMenu = (id: string, catalogItemIds: string[]) =>
  apiClient.post(Hosp_MenuAdmin_Menu_Items_Url(id), { items: catalogItemIds.map((catalogItemId, sortOrder) => ({ catalogItemId, sortOrder })) });
export const removeDishFromMenu = (id: string, itemId: string) => apiClient.delete(Hosp_MenuAdmin_Menu_Item_Url(id, itemId));

// Labels
export const listLabels = async (): Promise<Section[]> => {
  const { data } = await apiClient.get(Hosp_MenuAdmin_Labels_Url);
  const arr = Array.isArray(data) ? data : data?.items ?? [];
  return arr.filter((t: any) => t?.id).map((t: any) => ({ id: t.id, name: t.name }));
};
export const createLabel = (name: string) => apiClient.post(Hosp_MenuAdmin_Labels_Url, { name });
export const deleteLabel = (id: string) => apiClient.delete(Hosp_MenuAdmin_Label_Url(id));
export const setDishLabels = (id: string, tagNames: string[]) => apiClient.put(Hosp_MenuAdmin_Item_Labels_Url(id), { tagNames });

// Combos
export const addComboComponent = (id: string, childItemId: string) =>
  apiClient.post(Hosp_MenuAdmin_Item_Combo_Url(id), { childItemId });
export const removeComboComponent = (id: string, childId: string) =>
  apiClient.delete(Hosp_MenuAdmin_Item_Combo_Child_Url(id, childId));
